import { type Pt } from "./geometry"
import { type PatternKind } from "./document"

// Pattern layout math — pure, dependency-free, vitest-covered. Generates the
// tile/plank quads a room's pattern renders with, in world (sheet-px) space.
// The Konva side is a dumb clip-and-stroke: it never computes geometry.
//
// Model: tiles are generated axis-aligned in "pattern space" (rows along +x),
// then rotated by layout_angle about the room's anchor (its first outline
// vertex — stable under edits of other vertices, and a real corner to pivot
// on, which is where installers snap layout lines anyway). The culling window
// is the inverse-rotated view AABB, so cost is O(visible tiles).

export type ViewRect = { x: number; y: number; w: number; h: number }

export type PatternSpec = {
  wIn: number // tile/plank width in inches (row height)
  lIn: number // tile/plank length in inches (along the row)
  pattern: PatternKind
  angleDeg: number // rotation about anchor; canvas y-down, so positive reads clockwise
  anchor: Pt // sheet px
  ftPerPx: number
  seed: number // hashSeed(room.id) — stagger determinism
}

export const DEFAULT_MIN_TILE_PX = 4
export const DEFAULT_MAX_TILES = 12_000

// Suggested waste % per pattern (RT's seed numbers, 2026-08-06). Applied when
// a pattern is picked; the user can override per room afterward. Making this
// table app-editable is queued with M7's admin rule knobs (refinements.md).
export const WASTE_SUGGESTIONS: Record<PatternKind, number> = {
  straight: 10,
  brick: 10,
  third: 10,
  herringbone: 15,
  stagger: 10,
}

export function suggestedWaste(
  pattern: PatternKind | null | undefined,
  table: Record<PatternKind, number> = WASTE_SUGGESTIONS
): number | null {
  return pattern ? table[pattern] : null
}

/** ceil(order / carton), guarded against float noise (100/20 must be 5, not 6). */
export function cartonsFor(
  orderSf: number | null,
  sqftPerCarton: number | null | undefined
): number | null {
  if (orderSf == null || orderSf <= 0) return null
  if (sqftPerCarton == null || sqftPerCarton <= 0) return null
  return Math.ceil(orderSf / sqftPerCarton - 1e-9)
}

/** FNV-1a 32-bit — deterministic per-room stagger seed from the client uuid. */
export function hashSeed(id: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

// Per-row hash → PRNG. Stagger offsets MUST be a pure function of (seed, row
// index): culling changes which rows get generated, so a sequential PRNG
// stream would make planks jump as the user pans.
function hash2(seed: number, n: number): number {
  let h = seed ^ Math.imul(n + 0x9e3779b9, 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return h >>> 0
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const MIN_STAGGER = 0.15 // of plank length — joints closer than this get nudged
const NUDGE = 0.3

// Raw random offset for a stagger row (before the min-stagger nudge).
function rawStagger(seed: number, row: number, l: number): number {
  return mulberry32(hash2(seed, row))() * l
}

// Final offset compares against the PREVIOUS row's RAW offset (not its final
// one) so each row stays a pure function of its own index — the tiny chance
// of a nudge landing back near a nudged neighbor is invisible noise.
function staggerOffset(seed: number, row: number, l: number): number {
  const raw = rawStagger(seed, row, l)
  const prev = rawStagger(seed, row - 1, l)
  let dist = Math.abs(raw - prev)
  dist = Math.min(dist, l - dist) // circular distance
  return dist < MIN_STAGGER * l ? (raw + NUDGE * l) % l : raw
}

/** Row start offset in [0, l) for the row-family patterns (exported for tests). */
export function rowOffset(pattern: PatternKind, seed: number, row: number, l: number): number {
  switch (pattern) {
    case "straight":
      return 0
    case "brick":
      return (((row % 2) + 2) % 2) * (l / 2)
    case "third":
      return (((row % 3) + 3) % 3) * (l / 3)
    case "stagger":
      return staggerOffset(seed, row, l)
    case "herringbone":
      return 0 // herringbone doesn't use the row family
  }
}

/** Axis-aligned bbox of a ring, as a ViewRect (for the pattern clip window). */
export function ringBBox(ring: Pt[]): ViewRect {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const [x, y] of ring) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

/**
 * Generate the visible pattern quads for one room.
 *
 * Returns world-space quads as 8 floats per tile (x0,y0,x1,y1,x2,y2,x3,y3),
 * an EMPTY array when nothing is visible, or NULL when the pattern should not
 * be drawn at all (tiles below minTilePx on screen, or the visible tile count
 * over maxTiles) — callers fall back to the plain room tint on null.
 */
export function patternQuads(
  spec: PatternSpec,
  bbox: ViewRect,
  view: ViewRect,
  scale: number,
  opts: { minTilePx?: number; maxTiles?: number } = {}
): Float32Array | null {
  const { wIn, lIn, pattern, angleDeg, anchor, ftPerPx, seed } = spec
  if (!(wIn > 0) || !(lIn > 0) || !(ftPerPx > 0)) return null

  // Normalize so the long side runs along the row (+x); layout_angle handles
  // orientation.
  const aPx = wIn / 12 / ftPerPx
  const bPx = lIn / 12 / ftPerPx
  const w = Math.min(aPx, bPx)
  const l = Math.max(aPx, bPx)

  const minTilePx = opts.minTilePx ?? DEFAULT_MIN_TILE_PX
  const maxTiles = opts.maxTiles ?? DEFAULT_MAX_TILES
  if (w * scale < minTilePx) return null

  // World clip = view ∩ room bbox.
  const clipX0 = Math.max(view.x, bbox.x)
  const clipY0 = Math.max(view.y, bbox.y)
  const clipX1 = Math.min(view.x + view.w, bbox.x + bbox.w)
  const clipY1 = Math.min(view.y + view.h, bbox.y + bbox.h)
  if (clipX0 >= clipX1 || clipY0 >= clipY1) return new Float32Array(0)

  // Map the clip corners into pattern space (inverse rotation about anchor).
  const theta = (angleDeg * Math.PI) / 180
  const cos = Math.cos(theta)
  const sin = Math.sin(theta)
  const corners: Pt[] = [
    [clipX0, clipY0],
    [clipX1, clipY0],
    [clipX1, clipY1],
    [clipX0, clipY1],
  ]
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const [cx, cy] of corners) {
    const dx = cx - anchor[0]
    const dy = cy - anchor[1]
    const px = dx * cos + dy * sin // R(-θ)·d
    const py = -dx * sin + dy * cos
    if (px < minX) minX = px
    if (px > maxX) maxX = px
    if (py < minY) minY = py
    if (py > maxY) maxY = py
  }

  // Budget BEFORE generating: the pattern-space AABB is conservative (up to 2×
  // the true clip at 45°), which is the safe direction.
  if (((maxX - minX) * (maxY - minY)) / (w * l) > maxTiles) return null

  const out: number[] = []
  const push = (x0: number, y0: number, x1: number, y1: number) => {
    // Reject tiles fully outside the pattern-space window (half-open, so
    // edge-touching neighbors don't leak in).
    if (x1 <= minX || x0 >= maxX || y1 <= minY || y0 >= maxY) return
    // Rotate the four corners back to world space about the anchor.
    const cs: Pt[] = [
      [x0, y0],
      [x1, y0],
      [x1, y1],
      [x0, y1],
    ]
    for (const [px, py] of cs) {
      out.push(anchor[0] + px * cos - py * sin, anchor[1] + px * sin + py * cos)
    }
  }

  if (pattern === "herringbone") {
    // Exact herringbone tiling for ANY w ≤ l (no ratio constraint): the unit
    // cell is plank A (horizontal) at (0,0)-(l,w) plus plank B (vertical) at
    // (l,w−l)-(l+w,w), on the lattice t1=(w,w), t2=(l,−l) — |det| = 2wl, two
    // planks per cell, gapless (pinned by the exclusive-cover vitest).
    // Inverse: m = (x+y)/2w, n = (x−y)/2l.
    let m0 = Infinity
    let m1 = -Infinity
    let n0 = Infinity
    let n1 = -Infinity
    const patCorners: Pt[] = [
      [minX, minY],
      [maxX, minY],
      [maxX, maxY],
      [minX, maxY],
    ]
    for (const [x, y] of patCorners) {
      const m = (x + y) / (2 * w)
      const n = (x - y) / (2 * l)
      if (m < m0) m0 = m
      if (m > m1) m1 = m
      if (n < n0) n0 = n
      if (n > n1) n1 = n
    }
    for (let m = Math.floor(m0) - 1; m <= Math.ceil(m1) + 1; m++) {
      for (let n = Math.floor(n0) - 1; n <= Math.ceil(n1) + 1; n++) {
        const tx = m * w + n * l
        const ty = m * w - n * l
        push(tx, ty, tx + l, ty + w) // plank A (horizontal)
        push(tx + l, ty + w - l, tx + l + w, ty + w) // plank B (vertical)
      }
    }
  } else {
    const j0 = Math.floor(minY / w) - 1
    const j1 = Math.ceil(maxY / w) + 1
    for (let j = j0; j <= j1; j++) {
      const offset = rowOffset(pattern, seed, j, l)
      const i0 = Math.floor((minX - offset) / l) - 1
      const i1 = Math.ceil((maxX - offset) / l)
      for (let i = i0; i <= i1; i++) {
        push(offset + i * l, j * w, offset + (i + 1) * l, (j + 1) * w)
      }
    }
  }

  return Float32Array.from(out)
}
